// EXACT DECIMAL ARITHMETIC — the shared coefficient/scale core.
//
// A decimal number held as an integer COEFFICIENT at a declared decimal SCALE,
// over BigInt, so addition, subtraction, multiplication and ordering are exact.
// Binary floating point cannot represent most decimal amounts, so mathematically
// equal totals that arrive through different shapes land on different doubles:
// 0.10 + 0.20 is 0.30000000000000004 while a single 0.30 stays 0.3. Anything
// that DECIDES on a decimal quantity has to do the arithmetic here.
//
// #4779 amendment 18 — WHY THIS FILE EXISTS AT ALL (promote, don't copy).
// Amendment 17 wrote this algorithm a second time, in
// `apps/api/src/decision-modules/finance/finance.exact-money.ts`, because
// `ask-helix/contracts/assembly-spec.ts` already owned it but only behind
// module-private functions wired to that module's own refusal machinery. a17
// recorded the rule in its own header: "If a second decision module needs it,
// this is the file to promote, not to copy." #4805 needs the same primitive for
// segment-margin-cleanup and cost-to-serve, so the threshold is crossed and the
// PURE core is promoted here. There is now ONE implementation of
// coefficient/scale arithmetic in the tree:
//
//   * this module — the pure core. No unit, no currency, no receipts, no
//     refusal machinery, no rounding, no epsilon;
//   * `assembly-spec.ts` — KEEPS its unit/currency/receipt/refusal wrapper and
//     does its primitive arithmetic through this core;
//   * `finance.exact-money.ts` — a thin domain adapter over this core.
//
// WHAT IS DELIBERATELY ABSENT. No epsilon: a tolerance is a second, unstated
// decision rule that makes unequal amounts compare equal at a threshold nobody
// approved. No rounding: rounding belongs to display, strictly downstream of
// every decision. No fixed scale: scaling by 10^N would silently truncate a
// value whose contract admits more decimals than N, so the scale is taken from
// the OPERAND, never assumed.

/** A decimal number held exactly: coefficient / 10^scale. */
export type ExactDecimal = Readonly<{
  coefficient: bigint;
  scale: number;
}>;

export const EXACT_DECIMAL_ZERO: ExactDecimal = { coefficient: 0n, scale: 0 };

/** Compare decimal transport values exactly; never coerce an absent or nonnumeric value. */
export function exactDecimalValuesEqual(left: unknown, right: unknown): boolean {
  const decimal = (value: unknown): ExactDecimal | null => typeof value === 'string'
    ? exactDecimalFromText(value)
    : typeof value === 'number' && Number.isFinite(value) ? exactDecimalFromNumber(value) : null;
  const actual = decimal(left), expected = decimal(right);
  return actual !== null && expected !== null && compareExactDecimal(actual, expected) === 0;
}

const DECIMAL_TEXT = /^(-?)(\d+)(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/u;

/**
 * Beyond any real measure (a double spans about 1e-324 to 1e308). A larger exponent is junk:
 * "1e999999999" would build a billion-digit string (RangeError) and "1e-999999999" a scale whose
 * every later comparison computes 10 ** 999999999.
 */
const MAX_EXPONENT = 1000;

/**
 * Read plain or exponential base-10 decimal text exactly, at the scale the text
 * declares. Returns `null` for anything that is not such a decimal, including an
 * exponent beyond MAX_EXPONENT — callers that treat an unreadable value as an
 * error raise their own.
 */
export function exactDecimalFromText(text: string): ExactDecimal | null {
  const parsed = DECIMAL_TEXT.exec(text);
  if (!parsed) return null;
  const [, sign, integerDigits, fractionDigits = '', exponentText] = parsed;
  const exponent = exponentText ? Number(exponentText) : 0;
  if (Math.abs(exponent) > MAX_EXPONENT) return null;
  let digits = `${integerDigits}${fractionDigits}`;
  let scale = fractionDigits.length - exponent;
  if (scale < 0) {
    // A magnitude printed in exponential form with no fractional part left
    // (1e21 -> "1e+21"): shift the exponent into the digits, keeping it exact.
    digits += '0'.repeat(-scale);
    scale = 0;
  }
  const magnitude = BigInt(digits);
  return { coefficient: sign === '-' ? -magnitude : magnitude, scale };
}

/**
 * Read a number exactly, at the scale it was declared with.
 *
 * `Number.prototype.toString()` returns the SHORTEST decimal that round-trips
 * to the same double — that is, the decimal the value was written with before
 * it was carried over a binary channel. `(0.1).toString() === '0.1'`. Reading
 * the double's full binary expansion instead
 * (0.1000000000000000055511151231257827...) would reproduce the very defect
 * this module removes, because the exact binary expansions of 0.1 and 0.2 do
 * not sum to that of 0.3.
 */
export function exactDecimalFromNumber(value: number): ExactDecimal {
  if (!Number.isFinite(value)) {
    throw new Error('exactDecimalFromNumber requires a finite number.');
  }
  const text = value.toString();
  const parsed = exactDecimalFromText(text);
  if (!parsed) {
    throw new Error(`exactDecimalFromNumber cannot read the decimal "${text}".`);
  }
  return parsed;
}

export function addExactDecimal(left: ExactDecimal, right: ExactDecimal): ExactDecimal {
  const scale = Math.max(left.scale, right.scale);
  return {
    coefficient: exactDecimalAtScale(left, scale) + exactDecimalAtScale(right, scale),
    scale,
  };
}

export function subtractExactDecimal(left: ExactDecimal, right: ExactDecimal): ExactDecimal {
  const scale = Math.max(left.scale, right.scale);
  return {
    coefficient: exactDecimalAtScale(left, scale) - exactDecimalAtScale(right, scale),
    scale,
  };
}

/**
 * Exact product. Decimal multiplication is closed: the coefficients multiply
 * and the scales add, so a governed amount times a governed factor stays exact
 * with no rounding step anywhere.
 */
export function multiplyExactDecimal(left: ExactDecimal, right: ExactDecimal): ExactDecimal {
  return {
    coefficient: left.coefficient * right.coefficient,
    scale: left.scale + right.scale,
  };
}

/**
 * Quotient PROJECTED to a declared scale — the one closed form of decimal
 * division (#4779 amendment 19).
 *
 * An unbounded decimal quotient is not closed (1/3 has no finite decimal), so
 * the core deliberately offers no free division. What IS closed, pure and
 * deterministic is division to a DECLARED target scale: the mathematically
 * exact quotient, rounded once at that scale. This is the primitive behind a
 * published ratio with a declared presentation — the author uses it to state
 * the figure, and a verifier recomputing it from the same operands lands on
 * the same digits by construction, so "the stated ratio is the lawful
 * projection of its operands" is checkable by exact equality, with no epsilon
 * and no borrowed tolerance.
 *
 * ROUNDING MODE: round-half-away-from-zero, fixed — a declared mode, not a
 * parameter menu. It is sign-symmetric, needs no lookback digit, and on the
 * nonnegative domain governed ratios live in it agrees digit-for-digit with
 * the `Math.round`-based display rule it replaces, so promoting an author
 * from a float rounding onto this primitive changes no published value away
 * from a half boundary — and AT a half boundary it is the float that was
 * wrong.
 *
 * The single integer division happens after the scale shift, so no
 * intermediate result is ever lossy. A zero denominator states no quotient;
 * callers with a refusal vocabulary must refuse before calling.
 */
export function divideExactDecimalAtScale(
  numerator: ExactDecimal,
  denominator: ExactDecimal,
  scale: number,
): ExactDecimal {
  if (!Number.isSafeInteger(scale) || scale < 0) {
    throw new Error('divideExactDecimalAtScale requires a non-negative integer scale.');
  }
  if (denominator.coefficient === 0n) {
    throw new Error('divideExactDecimalAtScale requires a non-zero denominator.');
  }
  // value = (nc/10^ns) / (dc/10^ds); shifting by 10^(ds+scale) up front makes
  // the target-scale coefficient a single exact integer division.
  const dividend = numerator.coefficient * 10n ** BigInt(denominator.scale + scale);
  const divisor = denominator.coefficient * 10n ** BigInt(numerator.scale);
  const negative = dividend < 0n !== divisor < 0n;
  const absDividend = dividend < 0n ? -dividend : dividend;
  const absDivisor = divisor < 0n ? -divisor : divisor;
  let magnitude = absDividend / absDivisor;
  const remainder = absDividend % absDivisor;
  if (remainder * 2n >= absDivisor) magnitude += 1n;
  return { coefficient: negative ? -magnitude : magnitude, scale };
}

/** -1 / 0 / 1. The ONLY way a caller may order two exact decimals. */
export function compareExactDecimal(left: ExactDecimal, right: ExactDecimal): -1 | 0 | 1 {
  const scale = Math.max(left.scale, right.scale);
  const leftCoefficient = exactDecimalAtScale(left, scale);
  const rightCoefficient = exactDecimalAtScale(right, scale);
  if (leftCoefficient < rightCoefficient) return -1;
  if (leftCoefficient > rightCoefficient) return 1;
  return 0;
}

/**
 * The exact value as canonical plain decimal text — the form that carries the
 * amount without a binary channel in the middle.
 */
export function exactDecimalToText(value: ExactDecimal): string {
  const negative = value.coefficient < 0n;
  const digits = (negative ? -value.coefficient : value.coefficient).toString();
  const sign = negative ? '-' : '';
  if (value.scale === 0) return `${sign}${digits}`;
  const padded = digits.padStart(value.scale + 1, '0');
  return `${sign}${padded.slice(0, -value.scale)}.${padded.slice(-value.scale)}`;
}

/**
 * A ratio as an exact percent display, by moving the decimal point two places (0.1552 -> "15.52%",
 * 0.5 -> "50%"): no float, no rounding, every digit the engine computed. The one percent shift
 * every view shares (#9302 R2).
 */
export function exactDecimalPercentText(ratio: ExactDecimal): string {
  const percent = ratio.scale >= 2
    ? { coefficient: ratio.coefficient, scale: ratio.scale - 2 }
    : { coefficient: ratio.coefficient * 10n ** BigInt(2 - ratio.scale), scale: 0 };
  return `${exactDecimalToText(percent)}%`;
}

/**
 * Back to a JS number, for the DISPLAY path only.
 *
 * The conversion goes through the canonical decimal text so the result is the
 * double nearest the exact value — never a division that would re-introduce the
 * error this module exists to remove. Decisions never call this.
 */
export function exactDecimalToNumber(value: ExactDecimal): number {
  return Number(exactDecimalToText(value));
}

/** The coefficient this value would carry at `targetScale`. Exact, never lossy. */
export function exactDecimalAtScale(value: ExactDecimal, targetScale: number): bigint {
  return value.coefficient * 10n ** BigInt(targetScale - value.scale);
}
